// src/pages/Forms.js — single shared webhook + per-form field mapping
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  getForms, createForm, updateForm, deleteForm, getFormLogs, getUnmatchedLogs,
  getAllWebhookLogs, getWebhookSecret, regenerateWebhookSecret, getServices, getUsers,
} from '../utils/api';
import { Modal, EmptyState } from '../components/UI';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || `${window.location.origin}/api`;

// ── Field map metadata ────────────────────────────────────────────────────────

const FIELDS = [
  { key: 'name',    label: 'Name',    desc: 'Full name of the lead',            defaults: ['name', 'full_name', 'fullName', 'Full Name', 'your_name', 'Name'] },
  { key: 'email',   label: 'Email',   desc: 'Email address',                    defaults: ['email', 'email_address', 'emailAddress', 'Email'] },
  { key: 'phone',   label: 'Phone',   desc: 'Phone / mobile number',            defaults: ['phone', 'phone_number', 'phoneNumber', 'mobile', 'Mobile'] },
  { key: 'service', label: 'Service', desc: 'Enquiry type or service interest', defaults: ['service', 'interested_in', 'interest', 'Service'] },
  { key: 'company', label: 'Company', desc: 'Business or organisation name',    defaults: ['company', 'company_name', 'Company', 'business_name'] },
  { key: 'message', label: 'Message', desc: 'Notes, query or requirements',     defaults: ['message', 'notes', 'query', 'Message', 'description'] },
];

// ── Small helpers ─────────────────────────────────────────────────────────────

function CopyBtn({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="btn btn-outline btn-sm"
      onClick={() => { navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }}
    >
      <i className={`ti ${copied ? 'ti-check' : 'ti-copy'}`} /> {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

function LogStatusBadge({ status }) {
  const cls = { success: 's-converted', failed: 's-lost', duplicate: 's-notqualified' }[status] || 's-notqualified';
  return <span className={`status-badge ${cls}`}>{status}</span>;
}

// ── Tag input (type alias → Enter/comma to add, click × to remove) ───────────

function TagInput({ tags, onAdd, onRemove }) {
  const [val, setVal] = useState('');
  const inputRef = useRef(null);

  const commit = () => {
    const v = val.trim().replace(/,$/, '');
    if (v && !tags.includes(v)) onAdd(v);
    setVal('');
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
    if (e.key === 'Backspace' && !val && tags.length) onRemove(tags[tags.length - 1]);
  };

  return (
    <div
      style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center', padding: '5px 6px', cursor: 'text', minHeight: 34 }}
      onClick={() => inputRef.current?.focus()}
    >
      {tags.map(t => (
        <span
          key={t}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            background: 'var(--primary-light, #EEF2FF)', color: 'var(--primary, #4F46E5)',
            borderRadius: 5, padding: '1px 7px', fontSize: 11, fontWeight: 500,
            border: '0.5px solid var(--primary-border, #C7D2FE)',
          }}
        >
          {t}
          <span
            onClick={e => { e.stopPropagation(); onRemove(t); }}
            style={{ cursor: 'pointer', lineHeight: 1, opacity: 0.6, fontSize: 13, marginLeft: 1 }}
          >×</span>
        </span>
      ))}
      <input
        ref={inputRef}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={handleKey}
        onBlur={commit}
        placeholder={tags.length ? '' : 'Add alias…'}
        style={{ border: 'none', outline: 'none', fontSize: 12, minWidth: 90, flex: 1, background: 'transparent', fontFamily: 'inherit' }}
      />
    </div>
  );
}

// ── Visual field map builder ──────────────────────────────────────────────────

function ExtraLabels({ labels, onChange }) {
  const [keyInput, setKeyInput]     = useState('');
  const [labelInput, setLabelInput] = useState('');

  const add = () => {
    const k = keyInput.trim();
    const l = labelInput.trim();
    if (!k) return;
    onChange({ ...labels, [k]: l || k });
    setKeyInput(''); setLabelInput('');
  };

  const remove = (k) => {
    const next = { ...labels };
    delete next[k];
    onChange(next);
  };

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 4 }}>
        <label className="form-label" style={{ margin: 0 }}>Extra Field Labels <span style={{ fontWeight: 400, color: 'var(--gray-400)', fontSize: 11 }}>(optional)</span></label>
      </div>
      <p style={{ fontSize: 11, color: 'var(--gray-400)', margin: '0 0 8px', lineHeight: 1.5 }}>
        Any field beyond the 6 above is captured automatically in lead detail. Optionally give them readable labels here.
      </p>

      <div style={{ border: '0.5px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
        {Object.entries(labels).map(([k, l], i) => (
          <div key={k} style={{ display: 'grid', gridTemplateColumns: '1fr 16px 1fr 28px', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: i < Object.keys(labels).length - 1 ? '0.5px solid var(--gray-100)' : 'none', background: 'var(--white)' }}>
            <code style={{ fontSize: 11, color: 'var(--gray-600)' }}>{k}</code>
            <span style={{ color: 'var(--gray-300)', textAlign: 'center' }}>→</span>
            <span style={{ fontSize: 12, color: 'var(--gray-700)', fontWeight: 500 }}>{l}</span>
            <button type="button" onClick={() => remove(k)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', fontSize: 15, lineHeight: 1, padding: 0 }}>×</button>
          </div>
        ))}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 16px 1fr 28px', alignItems: 'center', gap: 8, padding: '6px 10px' }}>
          <input
            className="form-input"
            style={{ height: 30, fontSize: 12, padding: '0 7px' }}
            value={keyInput}
            onChange={e => setKeyInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && add()}
            placeholder="form field name"
          />
          <span style={{ color: 'var(--gray-300)', textAlign: 'center' }}>→</span>
          <input
            className="form-input"
            style={{ height: 30, fontSize: 12, padding: '0 7px' }}
            value={labelInput}
            onChange={e => setLabelInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && add()}
            placeholder="Display Label"
          />
          <button type="button" className="btn btn-outline btn-sm" style={{ padding: '0 8px', height: 30 }} onClick={add}>
            <i className="ti ti-plus" />
          </button>
        </div>
      </div>
    </div>
  );
}

function FieldMapBuilder({ value, onChange }) {
  const map = value || {};

  const addAlias    = (field, alias) => onChange({ ...map, [field]: [...(map[field] || []), alias] });
  const removeAlias = (field, alias) => onChange({ ...map, [field]: (map[field] || []).filter(a => a !== alias) });

  const labels = (map._labels && typeof map._labels === 'object') ? map._labels : {};
  const setLabels = (next) => onChange({ ...map, _labels: next });

  const hasAliases = FIELDS.some(f => (map[f.key] || []).length > 0);

  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
        <label className="form-label" style={{ margin: 0 }}>Field Mapping</label>
        {hasAliases && (
          <button type="button" className="btn btn-outline btn-sm" style={{ fontSize: 11, padding: '2px 8px' }}
            onClick={() => onChange({ _labels: labels })}>Clear aliases</button>
        )}
      </div>
      <p style={{ fontSize: 11, color: 'var(--gray-400)', margin: '0 0 8px' }}>
        Add aliases only if your form uses different question labels. Leave blank to rely on smart defaults.
        Press <kbd style={{ fontSize: 10, padding: '1px 4px', border: '0.5px solid var(--gray-200)', borderRadius: 3 }}>Enter</kbd> or comma after each alias.
      </p>

      <div style={{ border: '0.5px solid var(--gray-200)', borderRadius: 8, overflow: 'hidden' }}>
        {FIELDS.map((f, i) => (
          <div
            key={f.key}
            style={{
              display: 'grid', gridTemplateColumns: '110px 1fr',
              borderBottom: i < FIELDS.length - 1 ? '0.5px solid var(--gray-100)' : 'none',
            }}
          >
            <div style={{ padding: '8px 10px', background: 'var(--gray-50)', borderRight: '0.5px solid var(--gray-100)' }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--gray-700)' }}>{f.label}</div>
              <div style={{ fontSize: 10, color: 'var(--gray-400)', marginTop: 2, lineHeight: 1.3 }}>{f.desc}</div>
            </div>
            <div style={{ background: 'var(--white)' }}>
              <TagInput
                tags={map[f.key] || []}
                onAdd={alias => addAlias(f.key, alias)}
                onRemove={alias => removeAlias(f.key, alias)}
              />
              <div style={{ fontSize: 10, color: 'var(--gray-300)', padding: '0 8px 5px', lineHeight: 1.4 }}>
                Built-in: {f.defaults.join(', ')} …
              </div>
            </div>
          </div>
        ))}
      </div>

      <ExtraLabels labels={labels} onChange={setLabels} />
    </div>
  );
}

// ── Webhook setup card ────────────────────────────────────────────────────────

function WebhookSetupCard({ isAdmin, toast }) {
  const [secret, setSecret] = useState('');
  const [reveal, setReveal] = useState(false);
  const [busy,   setBusy]   = useState(false);

  useEffect(() => {
    if (!isAdmin) return; // secret is admin-only server-side too
    getWebhookSecret().then(r => setSecret(r.data.webhook_secret)).catch(() => {});
  }, [isAdmin]);

  const regenerate = async () => {
    if (!window.confirm('Regenerate the secret? Every Fillout form pointing to the current URL will stop working until you update it.')) return;
    setBusy(true);
    try { const r = await regenerateWebhookSecret(); setSecret(r.data.webhook_secret); setReveal(true); toast('Webhook secret regenerated'); }
    catch { toast('Failed to regenerate'); } finally { setBusy(false); }
  };

  const url = `${API_BASE}/webhook?secret=${secret || '••••'}`;

  if (!isAdmin) {
    return (
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="settings-section-title">Webhook URL</div>
        <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: -4, marginBottom: 0 }}>
          Only admins can view or manage the webhook URL and secret. Ask an admin if you need the setup link for a new form.
        </p>
      </div>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="settings-section-title">Webhook URL</div>
      <p style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: -4, marginBottom: 12 }}>
        Paste this single URL into every Fillout form's webhook settings (Integrations → Webhook).
        The EMS auto-detects which form sent the data from Fillout's form name — create a form profile below
        matching that name to apply custom field aliases and defaults.
      </p>

      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <code style={{ flex: 1, minWidth: 240, background: 'var(--gray-50)', padding: '8px 10px', borderRadius: 6, fontSize: 12, wordBreak: 'break-all' }}>
          {reveal ? url : `${API_BASE}/webhook?secret=${'•'.repeat(42)}`}
        </code>
        <button className="btn btn-outline btn-sm" onClick={() => setReveal(v => !v)}>
          <i className={`ti ${reveal ? 'ti-eye-off' : 'ti-eye'}`} /> {reveal ? 'Hide' : 'Reveal'}
        </button>
        <CopyBtn text={url} />
        {isAdmin && (
          <button className="btn btn-outline btn-sm" onClick={regenerate} disabled={busy}>
            <i className="ti ti-refresh" /> Regenerate
          </button>
        )}
      </div>

      <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 10, marginBottom: 0 }}>
        Alt: send secret as <code>X-Webhook-Secret</code> header. &nbsp;|&nbsp;
        Force a specific profile: append <code>&amp;form=YourFormName</code>.
      </p>
    </div>
  );
}

// ── Add / Edit form modal ─────────────────────────────────────────────────────

function parseFieldMap(raw) {
  if (!raw) return {};
  if (typeof raw === 'object') return raw;
  try { return JSON.parse(raw); } catch { return {}; }
}

const EMPTY_FORM = { name: '', match_value: '', default_service: '', default_assigned_to: '', is_active: 1, field_map: {} };

function FormModal({ open, onClose, form, services, users, onSaved, toast }) {
  const [data, setData] = useState(EMPTY_FORM);

  useEffect(() => {
    if (open) setData(form ? { ...form, field_map: parseFieldMap(form.field_map) } : EMPTY_FORM);
  }, [open, form]);

  const set = (k, v) => setData(d => ({ ...d, [k]: v }));

  const save = async () => {
    if (!data.name?.trim()) { toast('Form name required'); return; }
    try {
      const r = form ? await updateForm(form.id, data) : await createForm(data);
      onSaved(r.data);
      toast(form ? 'Form updated' : 'Form created');
      onClose();
    } catch { toast('Save failed'); }
  };

  return (
    <Modal open={open} onClose={onClose} title={form ? 'Edit Form Profile' : 'Add Form Profile'} width={660}>
      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Internal Name</label>
          <input className="form-input" value={data.name || ''} onChange={e => set('name', e.target.value)} placeholder="e.g. Homepage Contact Form" autoFocus />
        </div>
        <div className="form-group">
          <label className="form-label">Match Value</label>
          <input className="form-input" value={data.match_value || ''} onChange={e => set('match_value', e.target.value)} placeholder="Exact Fillout form name" />
        </div>
      </div>
      <p style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: -10, marginBottom: 14 }}>
        <strong>Match Value</strong> must equal the form's name in Fillout (case-insensitive), or the value you pass via <code>&amp;form=</code>.
        Not sure? Check "Unrecognized Submissions" on the main page — it shows what Fillout sent.
      </p>

      <div className="form-row">
        <div className="form-group">
          <label className="form-label">Default Service</label>
          <select className="form-input" value={data.default_service || ''} onChange={e => set('default_service', e.target.value)}>
            <option value="">— None —</option>
            {services.map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Default Assignee</label>
          <select className="form-input" value={data.default_assigned_to || ''} onChange={e => set('default_assigned_to', e.target.value)}>
            <option value="">— Unassigned —</option>
            {users.filter(u => u.is_active === undefined || Number(u.is_active) === 1)
                  .map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        </div>
      </div>

      <FieldMapBuilder value={data.field_map} onChange={v => set('field_map', v)} />

      {form && (
        <div className="settings-row" style={{ marginTop: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>Active</div>
          <input type="checkbox" checked={!!data.is_active} onChange={e => set('is_active', e.target.checked ? 1 : 0)} />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <button className="btn btn-outline btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save}>Save</button>
      </div>
    </Modal>
  );
}

// ── Per-form logs (expandable row) ───────────────────────────────────────────

function LogsPanel({ formId }) {
  const [logs, setLogs] = useState(null);
  useEffect(() => { getFormLogs(formId).then(r => setLogs(r.data)).catch(() => setLogs([])); }, [formId]);

  if (!logs) return <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-400)' }}>Loading…</div>;
  if (!logs.length) return <div style={{ padding: 16, fontSize: 12, color: 'var(--gray-400)' }}>No submissions for this form yet.</div>;

  return (
    <div style={{ padding: '8px 16px 16px' }}>
      <table className="leads-table">
        <thead><tr><th>Time</th><th>Status</th><th>Lead #</th><th>Detail</th></tr></thead>
        <tbody>
          {logs.map(l => (
            <tr key={l.id}>
              <td style={{ fontSize: 12 }}>{new Date(l.created_at).toLocaleString('en-IN')}</td>
              <td><LogStatusBadge status={l.status} /></td>
              <td style={{ fontSize: 12 }}>{l.lead_id ? `#${l.lead_id}` : '—'}</td>
              <td style={{ fontSize: 12, color: 'var(--gray-400)', maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {l.error_message || l.mapped_data}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Live webhook debug log ────────────────────────────────────────────────────

function prettyJSON(raw) {
  if (!raw) return '';
  try { return JSON.stringify(JSON.parse(raw), null, 2); }
  catch { return raw; }
}

function timeAgo(ts) {
  const secs = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (secs < 5)   return 'just now';
  if (secs < 60)  return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  return new Date(ts).toLocaleTimeString('en-IN');
}

function WebhookDebugLog() {
  const [logs,       setLogs]       = useState(null);
  const [expanded,   setExpanded]   = useState(null);
  const [lastFetch,  setLastFetch]  = useState(null);
  const [filter,     setFilter]     = useState('all');  // all | failed | unmatched
  const [tab,        setTab]        = useState({});     // per-row tab: 'mapped' | 'raw'
  const timerRef = useRef(null);

  const load = useCallback(() => {
    getAllWebhookLogs()
      .then(r => { setLogs(r.data); setLastFetch(new Date()); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 5000);
    return () => clearInterval(timerRef.current);
  }, [load]);

  const filtered = (logs || []).filter(l => {
    if (filter === 'failed')    return l.status === 'failed';
    if (filter === 'unmatched') return !l.form_id;
    return true;
  });

  const counts = (logs || []).reduce((a, l) => {
    a.total++;
    if (l.status === 'success')   a.success++;
    if (l.status === 'failed')    a.failed++;
    if (l.status === 'duplicate') a.dup++;
    if (!l.form_id)               a.unmatched++;
    return a;
  }, { total: 0, success: 0, failed: 0, dup: 0, unmatched: 0 });

  const rowTab = (id) => tab[id] || 'mapped';
  const setRowTab = (id, t) => setTab(prev => ({ ...prev, [id]: t }));

  return (
    <div className="card" style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <div className="settings-section-title" style={{ margin: 0, flex: 1 }}>Live Webhook Log</div>

        {/* Pulse dot */}
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: 'var(--gray-400)' }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--green, #16a34a)', display: 'inline-block', animation: 'pulse 2s infinite' }} />
          {lastFetch ? `Updated ${timeAgo(lastFetch)}` : 'Loading…'}
        </span>

        <button className="btn btn-outline btn-sm" onClick={load} style={{ fontSize: 11 }}>
          <i className="ti ti-refresh" /> Refresh
        </button>
      </div>

      {/* Summary chips */}
      {logs && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          {[
            { key: 'all',       label: `All  ${counts.total}` },
            { key: 'failed',    label: `Failed  ${counts.failed}`,       color: counts.failed    ? 'var(--red)'   : undefined },
            { key: 'unmatched', label: `Unmatched  ${counts.unmatched}`, color: counts.unmatched ? 'var(--amber)' : undefined },
          ].map(c => (
            <button
              key={c.key}
              className="btn btn-outline btn-sm"
              style={{ fontSize: 11, fontWeight: filter === c.key ? 700 : 400, borderColor: filter === c.key ? 'var(--black)' : undefined, color: c.color }}
              onClick={() => setFilter(c.key)}
            >{c.label}</button>
          ))}
        </div>
      )}

      {!logs ? (
        <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--gray-400)' }}>Loading…</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 24, textAlign: 'center', fontSize: 12, color: 'var(--gray-400)' }}>
          {logs.length === 0
            ? 'No webhook calls received yet. Send a test submission to your webhook URL and it will appear here within 5 seconds.'
            : 'No entries match this filter.'}
        </div>
      ) : (
        <table className="leads-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Form matched</th>
              <th>Received as</th>
              <th>Status</th>
              <th>Lead #</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(l => (
              <React.Fragment key={l.id}>
                <tr
                  style={{ cursor: 'pointer', background: expanded === l.id ? 'var(--gray-50)' : undefined }}
                  onClick={() => setExpanded(expanded === l.id ? null : l.id)}
                >
                  <td style={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                    <span title={new Date(l.created_at).toLocaleString('en-IN')}>{timeAgo(l.created_at)}</span>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    {l.form_name
                      ? <span style={{ fontWeight: 500 }}>{l.form_name}</span>
                      : <span style={{ color: 'var(--amber, #d97706)', fontSize: 11 }}>Unmatched</span>}
                  </td>
                  <td>
                    {l.form_name_received
                      ? <code style={{ fontSize: 10, background: 'var(--gray-100)', padding: '1px 5px', borderRadius: 3 }}>{l.form_name_received}</code>
                      : <span style={{ fontSize: 11, color: 'var(--gray-300)' }}>—</span>}
                  </td>
                  <td><LogStatusBadge status={l.status} /></td>
                  <td style={{ fontSize: 12 }}>{l.lead_id ? `#${l.lead_id}` : '—'}</td>
                  <td style={{ width: 24 }}>
                    <i className={`ti ${expanded === l.id ? 'ti-chevron-up' : 'ti-chevron-down'}`} style={{ fontSize: 13, color: 'var(--gray-400)' }} />
                  </td>
                </tr>

                {expanded === l.id && (
                  <tr>
                    <td colSpan={6} style={{ padding: 0, borderTop: '0.5px solid var(--gray-100)' }}>
                      <div style={{ padding: 12, background: 'var(--gray-50)' }}>

                        {/* Error banner */}
                        {l.error_message && (
                          <div style={{ background: '#FEF2F2', border: '0.5px solid #FECACA', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: 12, color: '#DC2626' }}>
                            <strong>Error:</strong> {l.error_message}
                          </div>
                        )}

                        {/* Tab switch */}
                        <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                          {['mapped', 'raw'].map(t => (
                            <button key={t} className="btn btn-outline btn-sm"
                              style={{ fontSize: 11, fontWeight: rowTab(l.id) === t ? 700 : 400, borderColor: rowTab(l.id) === t ? 'var(--black)' : undefined }}
                              onClick={() => setRowTab(l.id, t)}>
                              {t === 'mapped' ? 'Mapped Fields' : 'Raw Payload'}
                            </button>
                          ))}
                        </div>

                        {rowTab(l.id) === 'mapped' ? (
                          l.mapped_data ? (
                            <pre style={{ margin: 0, fontSize: 11, color: 'var(--gray-700)', background: 'var(--white)', border: '0.5px solid var(--gray-200)', borderRadius: 6, padding: '8px 10px', overflowX: 'auto', maxHeight: 220, lineHeight: 1.6 }}>
                              {prettyJSON(l.mapped_data)}
                            </pre>
                          ) : <p style={{ fontSize: 12, color: 'var(--gray-400)', margin: 0 }}>No mapped data recorded.</p>
                        ) : (
                          l.payload ? (
                            <pre style={{ margin: 0, fontSize: 11, color: 'var(--gray-700)', background: 'var(--white)', border: '0.5px solid var(--gray-200)', borderRadius: 6, padding: '8px 10px', overflowX: 'auto', maxHeight: 340, lineHeight: 1.6 }}>
                              {prettyJSON(l.payload)}
                            </pre>
                          ) : <p style={{ fontSize: 12, color: 'var(--gray-400)', margin: 0 }}>Raw payload not stored for this entry.</p>
                        )}

                        <div style={{ marginTop: 8, fontSize: 10, color: 'var(--gray-400)' }}>
                          IP: {l.ip_address || '—'} &nbsp;·&nbsp; Log #{l.id} &nbsp;·&nbsp; {new Date(l.created_at).toLocaleString('en-IN')}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function Forms({ toast }) {
  const { user, can } = useAuth();
  // Forms + webhook secret are gated on forms.manage; the prop name is kept as
  // `isAdmin` since that's how the child components consume it.
  const isAdmin   = can('forms.manage');

  const [forms,     setForms]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [services,  setServices]  = useState([]);
  const [users,     setUsers]     = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editForm,  setEditForm]  = useState(null);
  const [expanded,  setExpanded]  = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getForms().then(r => setForms(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    getServices().then(r => setServices(r.data)).catch(() => {});
    getUsers().then(r => setUsers(r.data)).catch(() => {});
  }, [load]);

  const onSaved = (saved) => {
    setForms(prev => {
      const idx = prev.findIndex(f => f.id === saved.id);
      return idx >= 0 ? prev.map(f => f.id === saved.id ? { ...f, ...saved } : f) : [...prev, saved];
    });
  };

  const onDelete = async (f) => {
    if (!window.confirm(`Delete form profile "${f.name}"? Existing leads stay, but they'll no longer be linked to this form.`)) return;
    try { await deleteForm(f.id); setForms(prev => prev.filter(x => x.id !== f.id)); toast('Form deleted'); }
    catch { toast('Failed to delete form'); }
  };

  const openEdit = (f) => { setEditForm(f); setShowModal(true); };

  return (
    <div>
      <div className="section-header">
        <div className="section-title">Forms &amp; Webhooks</div>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => { setEditForm(null); setShowModal(true); }}>
            <i className="ti ti-plus" /> Add Form
          </button>
        )}
      </div>

      <WebhookSetupCard isAdmin={isAdmin} toast={toast} />

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 32, textAlign: 'center', color: 'var(--gray-400)' }}>Loading…</div>
        ) : forms.length === 0 ? (
          <EmptyState icon="ti-forms" text="No form profiles yet — webhook submissions still work using built-in field defaults." />
        ) : (
          <table className="leads-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Match Value</th>
                <th>Leads</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {forms.map(f => (
                <React.Fragment key={f.id}>
                  <tr
                    style={{ cursor: 'pointer' }}
                    onClick={() => setExpanded(expanded === f.id ? null : f.id)}
                  >
                    <td style={{ fontWeight: 500 }}>{f.name}</td>
                    <td>
                      {f.match_value
                        ? <code style={{ fontSize: 11, background: 'var(--gray-50)', padding: '1px 5px', borderRadius: 4 }}>{f.match_value}</code>
                        : <span style={{ color: 'var(--gray-300)', fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ fontSize: 13 }}>{f.lead_count}</td>
                    <td>
                      <span className={`status-badge ${f.is_active ? 's-converted' : 's-lost'}`}>
                        {f.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-outline btn-sm" title="View recent logs">
                        <i className={`ti ${expanded === f.id ? 'ti-chevron-up' : 'ti-chevron-down'}`} />
                      </button>
                      {isAdmin && (
                        <>
                          <button className="btn btn-outline btn-sm" style={{ marginLeft: 4 }} onClick={() => openEdit(f)} title="Edit">
                            <i className="ti ti-edit" />
                          </button>
                          <button className="btn btn-outline btn-sm" style={{ marginLeft: 4 }} onClick={() => onDelete(f)} title="Delete">
                            <i className="ti ti-trash" />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>

                  {expanded === f.id && (
                    <tr>
                      <td colSpan={5} style={{ padding: 0, background: 'var(--gray-50)', borderTop: '0.5px solid var(--gray-100)' }}>
                        <LogsPanel formId={f.id} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <WebhookDebugLog />

      <FormModal
        open={showModal}
        onClose={() => setShowModal(false)}
        form={editForm}
        services={services}
        users={users}
        onSaved={onSaved}
        toast={toast}
      />
    </div>
  );
}
